import React, { useEffect, useRef, useState } from "react";
import { Typography, Avatar, Box, Slider, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import MusicNoteRounded from "@mui/icons-material/MusicNoteRounded";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import PauseRounded from "@mui/icons-material/PauseRounded";
import VolumeOffRounded from "@mui/icons-material/VolumeOffRounded";
import VolumeUpRounded from "@mui/icons-material/VolumeUpRounded";

interface PortfolioItem {
  id: number;
  url: string;
  type: string;
}

interface MediaItemProps {
  item: PortfolioItem;
  isActive: boolean;
  isMuted: boolean;
  onToggleMute: () => void;
}

const COMMON_MEDIA_STYLES: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "contain",
  borderRadius: 1,
};

const AudioCard = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(2),
  backgroundColor: "rgba(255, 255, 255, 0.05)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  borderRadius: 25,
  padding: theme.spacing(3),
  maxWidth: 280,
  height: 230,
  width: "100%",
  textAlign: "center",
}));

const Controls = styled("div")(({ theme }) => ({
  touchAction: "none",
  position: "absolute",
  bottom: 30,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 10,
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
  padding: "2px 8px",
  borderRadius: 40,
  zIndex: 1200,
  [theme.breakpoints.down("sm")]: {
    bottom: 20,
    width: "90%",
    gap: 6,
  },
}));

const TimeText = styled(Typography)(({ theme }) => ({
  color: "#ffffff",
  minWidth: 40,
}));

const SliderWrapper = styled(Box)(({ theme }) => ({
  width: "100%",
  "@media (max-width:600px)": {
    width: "100%",
  },
}));

const MediaSlider = styled(Slider)(({ theme }) => ({
  width: 150,
  color: "#ffffff",
  "& .MuiSlider-thumb": { width: 12, height: 12 },
  "& .MuiSlider-track": { border: "none" },
  "@media (max-width:600px)": { width: "100%" },
}));

const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60)
    .toString()
    .padStart(2, "0");
  const seconds = Math.floor(time % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const PortfolioMediaSlide: React.FC<MediaItemProps> = ({
  item,
  isActive,
  isMuted,
  onToggleMute,
}) => {
  const mediaRef = useRef<HTMLMediaElement | null>(null);
  const isSeekingRef = useRef(false);
  const type = item.type || "";

  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);

  useEffect(() => {
    isSeekingRef.current = isSeeking;
  }, [isSeeking]);

  useEffect(() => {
    const media = mediaRef.current;
    if (!media || !isActive) return;

    const updateTime = () => {
      if (!isSeekingRef.current && media.readyState >= 3) {
        const safeTime = Math.round(media.currentTime * 100) / 100;
        setCurrentTime((prev) =>
          Math.abs(prev - safeTime) > 0.2 ? safeTime : prev
        );
      }
    };

    media.addEventListener("timeupdate", updateTime);
    return () => {
      media.removeEventListener("timeupdate", updateTime);
    };
  }, [isActive]);

  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;

    if (!isActive) {
      media.pause();
      media.currentTime = 0;
      setCurrentTime(0);
      setIsPlaying(false);
      setIsSeeking(false);
    }
  }, [isActive]);

  const togglePlayPause = async () => {
    const media = mediaRef.current;
    if (!media) return;
    if (media.paused) {
      await media.play();
      setIsPlaying(true);
    } else {
      media.pause();
      setIsPlaying(false);
    }
  };

  const handleSliderChange = (_: Event, value: number | number[]) => {
    if (typeof value === "number") {
      setIsSeeking(true);
      setCurrentTime(value);
    }
  };

  const handleSliderChangeCommitted = (
    _: Event | React.SyntheticEvent<Element, Event>,
    value: number | number[]
  ) => {
    if (typeof value === "number" && mediaRef.current) {
      const media = mediaRef.current;
      if (media.readyState >= 2) {
        media.currentTime = value;
        setCurrentTime(value);
        setIsSeeking(false);
      } else {
        setCurrentTime(value);
        setIsSeeking(true);
      }
    }
  };

  const handleSliderTouchStart = (event: React.TouchEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    setIsSeeking(true);
  };

  const handleSliderMouseDown = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    event.stopPropagation();
    setIsSeeking(true);
  };

  const handleStopSeeking = () => {
    setIsSeeking(false);
  };

  const handleMediaLoadedMetadata = (
    event: React.SyntheticEvent<HTMLVideoElement | HTMLAudioElement>
  ) => {
    mediaRef.current = event.currentTarget;
    setDuration(event.currentTarget.duration);
  };

  const renderControls = () =>
    isActive &&
    (type.startsWith("video") || type.startsWith("audio")) && (
      <Controls>
        <IconButton onClick={togglePlayPause} size="small">
          {isPlaying ? (
            <PauseRounded sx={{ color: "#ffffff", fontSize: 28 }} />
          ) : (
            <PlayArrowRounded sx={{ color: "#ffffff", fontSize: 28 }} />
          )}
        </IconButton>
        <IconButton onClick={onToggleMute} size="small">
          {isMuted ? (
            <VolumeOffRounded sx={{ color: "#ffffff", fontSize: 22 }} />
          ) : (
            <VolumeUpRounded sx={{ color: "#ffffff", fontSize: 22 }} />
          )}
        </IconButton>
        <TimeText variant="caption">{formatTime(currentTime)}</TimeText>
        <SliderWrapper className="swiper-no-swiping">
          <MediaSlider
            step={0.1}
            max={duration}
            value={currentTime}
            onChange={handleSliderChange}
            onMouseUp={handleStopSeeking}
            onTouchEnd={handleStopSeeking}
            onMouseDown={handleSliderMouseDown}
            onTouchStart={handleSliderTouchStart}
            onChangeCommitted={handleSliderChangeCommitted}
          />
        </SliderWrapper>
        <TimeText variant="caption">{formatTime(duration)}</TimeText>
      </Controls>
    );

  if (type.startsWith("image"))
    return (
      <img
        src={item.url}
        alt="Portfolio Image"
        style={{ ...COMMON_MEDIA_STYLES }}
      />
    );

  if (type === "application/pdf")
    return <iframe src={item.url} style={{ ...COMMON_MEDIA_STYLES }} />;

  if (type.startsWith("video")) {
    return (
      <>
        <video
          playsInline
          src={item.url}
          muted={isMuted}
          controls={false}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onLoadedMetadata={handleMediaLoadedMetadata}
          style={{ ...COMMON_MEDIA_STYLES }}
          ref={mediaRef as React.RefObject<HTMLVideoElement>}
        />
        {renderControls()}
      </>
    );
  }

  if (type.startsWith("audio")) {
    return (
      <>
        <AudioCard>
          <audio
            src={item.url}
            muted={isMuted}
            controls={false}
            style={{ display: "none" }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onLoadedMetadata={handleMediaLoadedMetadata}
            ref={mediaRef as React.RefObject<HTMLAudioElement>}
          />
          <Avatar sx={{ bgcolor: "primary.main", width: 56, height: 56 }}>
            <MusicNoteRounded sx={{ color: "#fff", fontSize: 28 }} />
          </Avatar>
          <Typography
            noWrap
            variant="body2"
            sx={{ color: "#fff", maxWidth: "100%" }}
          >
            {item.url.split("/").pop()}
          </Typography>
        </AudioCard>
        {renderControls()}
      </>
    );
  }

  return <p style={{ color: "white" }}>Unsupported media type</p>;
};

export default PortfolioMediaSlide;
